import type { ChatMessage } from '../types';

type NetworkError = {
  statusCode?: number;
  status?: number;
  message?: string;
  response?: {
    status?: number;
    statusCode?: number;
  };
};

type GraphQLError = {
  message?: string;
};

type ApolloError = {
  networkError?: NetworkError;
  graphQLErrors?: GraphQLError[];
  message?: string;
};

const getNetworkErrorMessage = (networkError: NetworkError): string => {
  const statusCode =
    networkError.statusCode ||
    networkError.status ||
    networkError.response?.status ||
    networkError.response?.statusCode;

  if (statusCode === 504) {
    return 'Request timed out. The service may be experiencing high load. Please try again.';
  }

  if (networkError.message?.includes('CORS')) {
    return 'Network error: Unable to connect to the server. Please check your connection and try again.';
  }

  if (networkError.message === 'Failed to fetch' || networkError.message?.includes('fetch')) {
    return 'Failed to fetch response from the server. Please check your connection and try again.';
  }

  if (statusCode) {
    return `Network error (${statusCode}): ${
      networkError.message || 'Unable to connect to the server.'
    }`;
  }

  return `Network error: ${networkError.message || 'Unable to connect to the server.'}`;
};

const getGraphQLErrorMessage = (errors: GraphQLError[]): string => {
  return errors.map((e) => e.message || 'An error occurred').join('. ');
};

export const extractErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Failed to send message. Please try again.';
  }

  const apolloError = error as ApolloError;

  if (apolloError.networkError) {
    return getNetworkErrorMessage(apolloError.networkError);
  }

  if (apolloError.graphQLErrors && apolloError.graphQLErrors.length > 0) {
    return getGraphQLErrorMessage(apolloError.graphQLErrors);
  }

  if (apolloError.message) {
    return apolloError.message;
  }

  const fallbackMessage = error instanceof Error ? error.message : null;
  return fallbackMessage || 'Failed to send message. Please try again.';
};

export const createErrorMessage = (
  error: unknown,
  promptId: string,
  conversationId: string | undefined,
): ChatMessage => ({
  type: 'error',
  id: `${Date.now()}-error`,
  content: extractErrorMessage(error),
  conversationId: conversationId || '',
  createdAt: new Date().toISOString(),
  promptId,
});

export const createNoDataErrorMessage = (
  promptId: string,
  conversationId: string | undefined,
): ChatMessage => ({
  type: 'error',
  id: `${Date.now()}-error`,
  content: 'Failed to receive response from the server. Please try again.',
  conversationId: conversationId || '',
  createdAt: new Date().toISOString(),
  promptId,
});

export const STALLED_ERROR_CONTENT =
  "Your response couldn't be completed. Try sending your message again.";

export const STALE_ERROR_CONTENT = 'A previous response did not complete.';

export const createStalledErrorMessage = (
  promptId: string,
  conversationId: string | undefined,
): ChatMessage => ({
  type: 'error',
  id: `${Date.now()}-stalled`,
  content: STALLED_ERROR_CONTENT,
  conversationId: conversationId || '',
  createdAt: new Date().toISOString(),
  promptId,
});

export const createGraphQLResultErrorMessage = (
  errors: ReadonlyArray<{ message?: string }>,
  promptId: string,
  conversationId: string | undefined,
): ChatMessage => ({
  type: 'error',
  id: `${Date.now()}-error`,
  content: errors.map((e) => e.message || 'An error occurred').join('. '),
  conversationId: conversationId || '',
  createdAt: new Date().toISOString(),
  promptId,
});
